import { Component, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatListModule } from "@angular/material/list";
import { MatIconModule } from "@angular/material/icon";
import { RouterModule, Router, NavigationEnd } from "@angular/router";
import { HeaderComponent } from "./header/header.component";
import { FeedbackComponent } from "../components/feedback/feedback.component";
import { PartnersService } from "../services/partners.service";
import { OrganizationsService } from "../services/organizations.service";
import { Partner } from "../models/partner.model";
import { Organization } from "../models/organization.model";
import { BehaviorSubject, Subject, filter, takeUntil } from "rxjs";
import { DEFAULT_PARTNER_LOGO, DEFAULT_ORGANIZATION_LOGO } from "../shared/constants";

@Component({
	selector: "app-layout",
	standalone: true,
	imports: [
		CommonModule,
		MatSidenavModule,
		MatListModule,
		MatIconModule,
		RouterModule,
		HeaderComponent,
		FeedbackComponent,
	],
	templateUrl: "./layout.component.html",
	styleUrls: ["./layout.component.scss"],
})
export class LayoutComponent implements OnInit, OnDestroy {
	isSidebarOpen = true;
	private destroy$ = new Subject<void>();

	currentPartner$ = new BehaviorSubject<Partner | null>(null);
	currentOrganization$ = new BehaviorSubject<Organization | null>(null);

	// Default logos
	readonly DEFAULT_PARTNER_LOGO = DEFAULT_PARTNER_LOGO;
	readonly DEFAULT_ORGANIZATION_LOGO = DEFAULT_ORGANIZATION_LOGO;

	constructor(
		private router: Router,
		private partnersService: PartnersService,
		private organizationsService: OrganizationsService,
	) {}

	ngOnInit() {
		// Listen to route changes and update context
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntil(this.destroy$),
			)
			.subscribe(() => {
				this.updateContext();
			});

		// Update context on initial load
		this.updateContext();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private updateContext() {
		// Clear current context
		this.currentPartner$.next(null);
		this.currentOrganization$.next(null);

		// Load partner data if in partner view
		if (this.isPartnerView()) {
			const partnerId = this.getPartnerId();
			this.partnersService
				.getPartner(partnerId)
				.pipe(takeUntil(this.destroy$))
				.subscribe((partner) => {
					this.currentPartner$.next(partner);
				});
		}

		// Load organization data if in organization view
		if (this.isOrganizationView()) {
			const organizationId = this.getOrganizationId();
			this.organizationsService
				.getOrganization(organizationId)
				.pipe(takeUntil(this.destroy$))
				.subscribe((organization) => {
					this.currentOrganization$.next(organization);
				});
		}
	}

	onToggleSidebar() {
		this.isSidebarOpen = !this.isSidebarOpen;
	}

	isRouteActive(path: string): boolean {
		return this.router.url.startsWith(path);
	}

	isPartnerView(): boolean {
		const url = this.router.url;
		return url.startsWith("/partners/") && url.split("/").length > 2;
	}

	isOrganizationView(): boolean {
		const url = this.router.url;
		return (
			url.startsWith("/organizations/") &&
			url.split("/").length > 2 &&
			url !== "/organizations/new"
		);
	}

	getPartnerId(): string {
		const url = this.router.url;
		const segments = url.split("/");
		return segments[2]; // The partner ID is the third segment in the URL
	}

	getOrganizationId(): string {
		const url = this.router.url;
		const segments = url.split("/");
		return segments[2]; // The organization ID is the third segment in the URL
	}

	onPartnerLogoError(event: Event): void {
		const img = event.target as HTMLImageElement;
		img.src = this.DEFAULT_PARTNER_LOGO;
	}

	onOrganizationLogoError(event: Event): void {
		const img = event.target as HTMLImageElement;
		img.src = this.DEFAULT_ORGANIZATION_LOGO;
	}
}
